import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  formatCents,
  IMPORT_ROW_LIMIT,
  buildCustomerRows,
  buildJobRows,
  fieldsFor,
  guessMapping,
  parseCsv,
  type ImportEntity,
  type ImportReport,
  type Mapping,
  type RowProblem,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useCrumb } from '../components/Layout';
import { Button, Card, Field, PageHeader, Select } from '../components/ui';

/**
 * Moving in from a spreadsheet.
 *
 * Four steps down one page rather than a wizard, because the thing somebody
 * needs most here is to be able to look back at what they just told it. The
 * file, the columns, what it will do, and what it did — all still on screen
 * when the report appears.
 *
 * Everything up to the last step happens in this browser. The file is never
 * uploaded: it is read locally, and only the rows that survived parsing are
 * sent. That is partly privacy — a customer list is the most valuable thing a
 * contractor owns and most of them will be trying this before they trust us —
 * and partly that a mapping mistake should cost a click, not a round trip.
 */
export function ImportPage() {
  useCrumb('Import');

  const qc = useQueryClient();

  /*
   * Which of the two is being imported lives in the URL, so the Import
   * buttons on the customers and jobs screens can land somebody on the right
   * one — and so the back button steps between them rather than out of the
   * page. Anything unrecognised falls back to customers rather than erroring:
   * a mistyped link should still show a usable screen.
   */
  const [search, setSearch] = useSearchParams();
  const requested = search.get('type');
  const entity: ImportEntity = requested === 'jobs' ? 'jobs' : 'customers';
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [readError, setReadError] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);

  const fields = fieldsFor(entity);

  /*
   * Re-guess when the entity changes without going through the button — the
   * back button, or a link straight to ?type=jobs with a file already loaded.
   * The two field sets share almost no keys, so a mapping guessed for
   * customers reads as "every required column is missing" against jobs, which
   * looks like a broken screen rather than a changed one.
   */
  const guessedFor = useRef<ImportEntity>(entity);
  useEffect(() => {
    if (guessedFor.current === entity) return;
    guessedFor.current = entity;
    if (headers.length > 0) setMapping(guessMapping(headers, entity));
  }, [entity, headers]);

  /*
   * The rows, rebuilt on every mapping change. Cheap enough to do eagerly —
   * it is string shuffling over at most two thousand rows — and doing it
   * eagerly is what lets the preview below answer "what will this do" before
   * anything is sent.
   */
  const built = useMemo(() => {
    if (dataRows.length === 0) return null;
    return entity === 'customers'
      ? { kind: 'customers' as const, ...buildCustomerRows(dataRows, mapping) }
      : { kind: 'jobs' as const, ...buildJobRows(dataRows, mapping) };
  }, [entity, dataRows, mapping]);

  function reset() {
    setFileName(null);
    setHeaders([]);
    setDataRows([]);
    setMapping({});
    setReadError(null);
    setReport(null);
  }

  async function onFile(file: File) {
    setReadError(null);
    setReport(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) {
        setReadError('That file has a header row and nothing else.');
        return;
      }
      const [head, ...rest] = rows;
      if (rest.length > IMPORT_ROW_LIMIT) {
        setReadError(
          `That file has ${rest.length.toLocaleString()} rows. The limit is ` +
            `${IMPORT_ROW_LIMIT.toLocaleString()} at a time — split it and run it twice.`,
        );
        return;
      }
      setFileName(file.name);
      setHeaders(head ?? []);
      setDataRows(rest);
      setMapping(guessMapping(head ?? [], entity));
    } catch {
      setReadError('That file could not be read. It needs to be a .csv.');
    }
  }

  /* The URL is the state; the effect above re-guesses off the back of it. */
  function switchEntity(next: ImportEntity) {
    setSearch(next === 'customers' ? {} : { type: next });
    setReport(null);
  }

  const missingRequired = fields.filter((f) => f.required && mapping[f.key] == null);

  const run = useMutation({
    mutationFn: async () => {
      if (!built) throw new Error('Nothing to import');
      return built.kind === 'customers'
        ? api.imports.customers(built.rows)
        : api.imports.jobs(built.rows);
    },
    onSuccess: (result) => {
      setReport(result);
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  return (
    <>
      {/*
        The page scrolls itself. The layout puts every screen inside an
        `overflow-hidden` flex column, so a page without its own scroller is
        simply cut off at the fold — which is what happened here: a real
        import of 878 rows could not reach the mapping controls below the
        first screenful, let alone the button.
      */}
      <PageHeader
        eyebrow="Business"
        title="Import"
        subtitle="Bring customers and jobs in from a spreadsheet"
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px] pb-10">
        <div className="mx-auto w-full max-w-4xl space-y-4 py-5">
      <Card title="What are you importing?" icon="users">
        <div className="space-y-3 px-4 py-4">
          <div className="flex gap-2">
            {(['customers', 'jobs'] as const).map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => switchEntity(id)}
                className={
                  entity === id
                    ? 'rounded-lg bg-accent px-3 py-2 text-[13px] font-semibold text-white'
                    : 'rounded-lg border border-line px-3 py-2 text-[13px] font-medium text-ink-2'
                }
              >
                {id === 'customers' ? 'Customers' : 'Jobs'}
              </button>
            ))}
          </div>
          <p className="text-[12.5px] leading-relaxed text-ink-3">
            {entity === 'customers'
              ? 'One row per customer. An address is optional — without one you get a contact list, which can be booked later.'
              : 'One row per job. Each row carries its customer; anyone not already on file is created from the row, so one file can move a whole business in.'}
          </p>
        </div>
      </Card>

      <Card title="The file" icon="file">
        <div className="space-y-3 px-4 py-4">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
              /* So choosing the same file twice still fires a change. */
              e.target.value = '';
            }}
            className="block w-full text-[13px] text-ink-2 file:mr-3 file:rounded-lg file:border-0 file:bg-raised file:px-3 file:py-2 file:text-[13px] file:font-semibold file:text-ink"
          />

          {readError && (
            <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">
              {readError}
            </p>
          )}

          {fileName && (
            <p className="text-[12.5px] text-ink-3">
              <span className="font-medium text-ink-2">{fileName}</span> — {headers.length} columns,{' '}
              {dataRows.length.toLocaleString()} rows.{' '}
              <button type="button" onClick={reset} className="font-semibold text-accent">
                Start over
              </button>
            </p>
          )}

          <p className="text-[12px] leading-relaxed text-ink-4">
            The file is read here in your browser. Only the rows themselves are sent, after you
            have seen what they will do.
          </p>
        </div>
      </Card>

      {headers.length > 0 && (
        <Card title="Which column is which?" icon="grid">
          <div className="space-y-3 px-4 py-4">
            <p className="text-[12.5px] leading-relaxed text-ink-3">
              Guessed from your headers. Change anything that is wrong — a column set to
              &ldquo;Not imported&rdquo; is simply left out.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map((field) => (
                <Field
                  key={field.key}
                  label={field.label}
                  hint={field.hint}
                  required={field.required}
                  error={
                    field.required && mapping[field.key] == null ? 'needs a column' : null
                  }
                >
                  <Select
                    value={mapping[field.key] ?? ''}
                    onChange={(e) =>
                      setMapping((m) => ({
                        ...m,
                        [field.key]: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  >
                    <option value="">Not imported</option>
                    {headers.map((header, index) => (
                      <option key={index} value={index}>
                        {header || `Column ${index + 1}`}
                      </option>
                    ))}
                  </Select>
                </Field>
              ))}
            </div>
          </div>
        </Card>
      )}

      {built && (
        <Card title="What this will do" icon="chart">
          <div className="space-y-3 px-4 py-4">
            {missingRequired.length > 0 ? (
              <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">
                {missingRequired.map((f) => f.label).join(' and ')}{' '}
                {missingRequired.length === 1 ? 'needs' : 'need'} a column before anything can be
                imported.
              </p>
            ) : (
              <p className="text-[13px] text-ink-2">
                <span className="font-semibold text-ink">{built.rows.length.toLocaleString()}</span>{' '}
                {entity === 'customers' ? 'customers' : 'jobs'} ready.{' '}
                {entity === 'customers' &&
                  'Anyone already on file is recognised by phone or email and left alone.'}
              </p>
            )}

            <ProblemList problems={built.problems} />

            <PreviewTable entity={entity} built={built} />

            <div className="flex items-center gap-3 pt-1">
              <Button
                variant="primary"
                icon="check"
                disabled={missingRequired.length > 0 || built.rows.length === 0}
                loading={run.isPending}
                onClick={() => run.mutate()}
              >
                Import {built.rows.length.toLocaleString()}{' '}
                {entity === 'customers' ? 'customers' : 'jobs'}
              </Button>
              {run.isError && (
                <span className="text-[13px] text-danger">
                  {(run.error as { message?: string }).message ?? 'Import failed'}
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

          {report && <ReportCard report={report} entity={entity} />}
        </div>
      </div>
    </>
  );
}

/**
 * What was wrong with the file, before anything is sent.
 *
 * Capped at a dozen because the twentieth copy of the same complaint teaches
 * nothing the first taught — and because a wall of red makes people abandon
 * an import that was eight hundred rows of fine.
 */
function ProblemList({ problems }: { problems: RowProblem[] }) {
  if (problems.length === 0) return null;
  const shown = problems.slice(0, 12);

  return (
    <div className="rounded-lg bg-warn-soft px-3 py-2.5">
      <p className="text-[12.5px] font-semibold text-warn">
        {problems.length} {problems.length === 1 ? 'row needs' : 'rows need'} a look
      </p>
      <ul className="mt-1.5 space-y-1">
        {shown.map((p, i) => (
          <li key={i} className="text-[12.5px] leading-relaxed text-ink-2">
            <span className="font-medium">Line {p.line}</span> — {p.message}
          </li>
        ))}
      </ul>
      {problems.length > shown.length && (
        <p className="mt-1.5 text-[12px] text-ink-3">
          and {problems.length - shown.length} more like these.
        </p>
      )}
    </div>
  );
}

/** The first few rows as they will be read, so a wrong column is obvious. */
function PreviewTable({
  entity,
  built,
}: {
  entity: ImportEntity;
  built: { kind: 'customers' | 'jobs'; rows: unknown[] };
}) {
  const rows = built.rows.slice(0, 5);
  if (rows.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full text-[12.5px]">
        <thead className="bg-raised text-ink-3">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Line</th>
            <th className="px-3 py-2 text-left font-medium">
              {entity === 'customers' ? 'Name' : 'Job'}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {entity === 'customers' ? 'Contact' : 'Customer'}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {entity === 'customers' ? 'Address' : 'Status & value'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((raw, i) => {
            const row = raw as Record<string, never>;
            const cells =
              entity === 'customers'
                ? customerPreview(row as never)
                : jobPreview(row as never);
            return (
              <tr key={i} className="border-t border-line">
                <td className="px-3 py-2 text-ink-4">{cells.line}</td>
                <td className="px-3 py-2 font-medium text-ink">{cells.first}</td>
                <td className="px-3 py-2 text-ink-2">{cells.second}</td>
                <td className="px-3 py-2 text-ink-2">{cells.third}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type PreviewCells = { line: number; first: string; second: string; third: string };

function customerPreview(row: {
  line: number;
  name: string;
  phone: string | null;
  email: string | null;
  property: { line1: string; city: string } | null;
}): PreviewCells {
  return {
    line: row.line,
    first: row.name,
    second: [row.phone, row.email].filter(Boolean).join(' · ') || '—',
    third: row.property ? `${row.property.line1}, ${row.property.city}` : '—',
  };
}

function jobPreview(row: {
  line: number;
  title: string;
  jobType: string | null;
  status: string | null;
  amountCents: number | null;
  scheduledStart: string | null;
  customer: { name: string };
}): PreviewCells {
  /*
   * Status and amount share the last column. They are the two fields most
   * likely to be mapped to the wrong thing in a wide export, and seeing
   * "completed · $1,249.00" against a job is how somebody catches it before
   * eight hundred rows go in.
   */
  const when = row.scheduledStart
    ? row.scheduledStart.replace('T', ' ').slice(0, 16)
    : 'unscheduled';
  const money = row.amountCents !== null ? formatCents(row.amountCents) : null;

  return {
    line: row.line,
    first: row.title,
    second: row.customer.name,
    third: [row.status ?? when, money].filter(Boolean).join(' · '),
  };
}

/**
 * What actually happened.
 *
 * Counts first, then every failure with its line number. Not a toast: an
 * import is the one action somebody will want to read twice, and a message
 * that disappears after four seconds is no use when the question is which
 * eleven rows out of eight hundred did not go in.
 */
function ReportCard({ report, entity }: { report: ImportReport; entity: ImportEntity }) {
  return (
    <Card title="Done" icon="check">
      <div className="space-y-3 px-4 py-4">
        <div className="flex flex-wrap gap-4">
          <Figure label="Created" value={report.created} tone="good" />
          {entity === 'customers' && (
            <Figure label="Already on file" value={report.matched} tone="plain" />
          )}
          <Figure label="Failed" value={report.failed} tone={report.failed > 0 ? 'bad' : 'plain'} />
        </div>

        {report.failures.length > 0 ? (
          <div className="rounded-lg bg-danger-soft px-3 py-2.5">
            <p className="text-[12.5px] font-semibold text-danger">
              These rows were not imported. Everything else was — fix these and import the file
              again; the rows that went in will be recognised rather than duplicated.
            </p>
            <ul className="mt-1.5 space-y-1">
              {report.failures.map((f) => (
                <li key={f.line} className="text-[12.5px] leading-relaxed text-ink-2">
                  <span className="font-medium">Line {f.line}</span> — {f.name}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-[13px] text-ink-2">Every row went in.</p>
        )}
      </div>
    </Card>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'good' | 'bad' | 'plain';
}) {
  const colour =
    tone === 'good' ? 'text-good' : tone === 'bad' ? 'text-danger' : 'text-ink';
  return (
    <div>
      <div className={`text-[22px] font-semibold tabular-nums ${colour}`}>
        {value.toLocaleString()}
      </div>
      <div className="text-[12px] text-ink-3">{label}</div>
    </div>
  );
}
